import clsx from 'clsx';
import type { DetailedHTMLProps, InputHTMLAttributes } from 'react';
import { BiSolidChevronDown, BiX } from 'react-icons/bi';

import s from './inputs.module.scss';

export const Input = ({
  label,
  error,
  mandatory,
  className,
  suffix,
  suffixNode,
  startIcon,
  onClear,
  saved,
  units,
  unit,
  onUnitChange,
  ref,
  ...props
}: {
  label?: string;
  error?: string;
  mandatory?: boolean;
  suffix?: string;
  /** Interactive node rendered in the suffix position (pointer-events enabled). */
  suffixNode?: React.ReactNode;
  startIcon?: React.ReactNode;
  onClear?: () => void;
  /**
   * The field after it has been saved and left alone: label above the value, one
   * hairline underneath, no box.
   *
   * The same component rather than a separate read-only one, because a section that
   * edits and then reads — Create CV, the profile cards — should flip a prop instead of
   * swapping components and hoping the two stay described the same way. There is no
   * input at all in this state: a disabled box still looks like somewhere to type.
   */
  saved?: boolean;
  /**
   * Turns the suffix into a choice the caller owns: given more than one, the slot becomes
   * a button that cycles through them and reports the new one, so the unit lives where
   * the number is typed rather than in a settings screen.
   *
   * Not `UnitInput`: that one reads the user's saved unit preference and converts the
   * value when it changes, which is right for a field whose unit is an account setting.
   * This is for a unit the screen itself owns — a form that offers both and converts its
   * own fields in one go.
   */
  units?: readonly string[];
  unit?: string;
  onUnitChange?: (next: string) => void;
} & DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>) => {
  const showClear = !!onClear && !!props.value;
  const unitChoice = !!units && units.length > 1 && unit !== undefined && !!onUnitChange;

  if (saved) {
    return (
      <div className={clsx(s.InputRoot, s.Saved, className)}>
        {label && <div className={s.Label}>{label}</div>}
        {/* An em dash for a field that was saved empty — the row still says the
            question was asked and answered with nothing. */}
        <p className={s.SavedValue}>
          {props.value === '' || props.value === undefined ? '—' : String(props.value)}
        </p>
      </div>
    );
  }

  return (
    <label className={clsx(s.InputRoot, className)}>
      {label && (
        <div className={s.Label}>
          <span>{label}</span>
          {mandatory && <span>*</span>}
        </div>
      )}
      <div
        className={clsx(
          s.InputWrapper,
          error && s.error,
          (suffix || suffixNode || unitChoice) && s.withIcon,
          suffixNode && s.withSuffixNode,
          startIcon && s.withPrefix,
          showClear && s.withClear,
        )}
      >
        {startIcon && <span className={s.prefix}>{startIcon}</span>}
        <input type="text" className="body-medium" {...props} ref={ref} />
        {unitChoice ? (
          <button
            type="button"
            className={clsx(s.suffix, s.unitToggle)}
            /* Inside a <label>: without this a click would be forwarded to the input and
               the toggle would only ever focus the field. */
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.preventDefault();
              onUnitChange!(units![(units!.indexOf(unit!) + 1) % units!.length]);
            }}
            aria-label={`Change Unit (${unit})`}
            title="Change Unit"
          >
            {unit}
            {/* The cue that this is a control at all: a bare "ft" where a suffix normally
                sits reads as a label, and nobody clicks a label. */}
            <BiSolidChevronDown className={s.unitCaret} aria-hidden="true" />
          </button>
        ) : (
          suffix && <span className={s.suffix}>{suffix}</span>
        )}
        {suffixNode && <span className={s.suffixInteractive}>{suffixNode}</span>}
        {showClear && (
          <button
            type="button"
            className={s.clear}
            aria-label="Clear search"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClear}
          >
            <BiX />
          </button>
        )}
      </div>
      {!!error && <p className={s.Error}>{error}</p>}
    </label>
  );
};
